import api from '../services/api';

// Cache for admin settings to avoid repeated API calls
let adminSettingsCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get admin settings with caching
const getAdminSettings = async () => {
  const now = Date.now();
  if (adminSettingsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return adminSettingsCache;
  }

  try {
    const response = await api.get('/admin/settings');
    adminSettingsCache = response.data;
    cacheTimestamp = now;
    return adminSettingsCache;
  } catch (error) {
    console.error('Failed to fetch admin settings for custom ID generation:', error);
    return null;
  }
};

// Get nested value from object using dot notation
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    if (current && typeof current === 'object') {
      // Handle array indices like 'authorizedSignatories[0].name'
      const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
      if (arrayMatch) {
        const arrayKey = arrayMatch[1];
        const index = parseInt(arrayMatch[2]);
        return current[arrayKey] && current[arrayKey][index];
      }
      return current[key];
    }
    return undefined;
  }, obj);
};

// Generate custom ID for a record
export const generateCustomId = async (collectionName, record) => {
  try {
    const settings = await getAdminSettings();
    if (!settings?.customIdConfig?.enabled) {
      return record._id;
    }

    const config = settings.customIdConfig;
    const template = config.templates[collectionName]?.template;
    
    if (!template) {
      return record._id;
    }

    let customId = template;
    const dateFormat = config.dateFormat || 'DDMMYYYY';
    const separator = config.separator || '-';
    const padding = config.padding || 4;

    // Replace date placeholders
    const now = new Date();
    const dateMap = {
      'DDMMYYYY': now.getDate().toString().padStart(2, '0') + 
                  (now.getMonth() + 1).toString().padStart(2, '0') + 
                  now.getFullYear().toString(),
      'MMDDYYYY': (now.getMonth() + 1).toString().padStart(2, '0') + 
                  now.getDate().toString().padStart(2, '0') + 
                  now.getFullYear().toString(),
      'YYYYMMDD': now.getFullYear().toString() + 
                  (now.getMonth() + 1).toString().padStart(2, '0') + 
                  now.getDate().toString().padStart(2, '0'),
      'DD-MM-YYYY': now.getDate().toString().padStart(2, '0') + '-' + 
                    (now.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                    now.getFullYear().toString(),
      'MM-DD-YYYY': (now.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                    now.getDate().toString().padStart(2, '0') + '-' + 
                    now.getFullYear().toString(),
      'YYYY-MM-DD': now.getFullYear().toString() + '-' + 
                    (now.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                    now.getDate().toString().padStart(2, '0')
    };

    customId = customId.replace(`{${dateFormat}}`, dateMap[dateFormat] || dateMap['DDMMYYYY']);

    // Replace special placeholders
    if (customId.includes('{UNIQUE_ID}')) {
      const sequence = record.sequence || 1;
      customId = customId.replace('{UNIQUE_ID}', sequence.toString().padStart(padding, '0'));
    }

    if (customId.includes('{MASTER_DATA_ID}') && record.masterDataId) {
      const masterDataId = typeof record.masterDataId === 'object' ? record.masterDataId._id : record.masterDataId;
      customId = customId.replace('{MASTER_DATA_ID}', masterDataId);
    }

    // Replace field placeholders from the record data
    const fieldPlaceholders = customId.match(/\{([^}]+)\}/g) || [];
    fieldPlaceholders.forEach(placeholder => {
      const fieldName = placeholder.slice(1, -1); // Remove { and }
      
      // Skip if it's a special placeholder we already handled
      if (['UNIQUE_ID', 'MASTER_DATA_ID', dateFormat].includes(fieldName)) {
        return;
      }

      // Try to get the field value from the record
      const fieldValue = getNestedValue(record, fieldName);
      if (fieldValue !== undefined) {
        customId = customId.replace(placeholder, fieldValue.toString());
      } else {
        // If field not found, replace with empty string
        customId = customId.replace(placeholder, '');
      }
    });

    return customId || record._id;
  } catch (error) {
    console.error(`Error generating custom ID for ${collectionName}:`, error);
    return record._id;
  }
};

// Generate custom IDs for a list of records
export const generateCustomIdsForList = async (collectionName, records) => {
  if (!Array.isArray(records)) {
    return records;
  }

  const updatedRecords = await Promise.all(
    records.map(async (record) => ({
      ...record,
      customId: await generateCustomId(collectionName, record)
    }))
  );

  return updatedRecords;
};

// Clear cache (useful when admin settings are updated)
export const clearCustomIdCache = () => {
  adminSettingsCache = null;
  cacheTimestamp = 0;
}; 